import { useEffect, useRef, useState } from "react";
import { Save, Image as ImageIcon, Palette, Phone, Mail, MapPin, Globe, FileText, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const DEFAULT = {
  brand_name: "", logo_data_url: "",
  primary_color: "#0e7490", accent_color: "#0891b2",
  contact_phone: "", contact_email: "", contact_address: "",
  website: "", disclaimer: "",
};

export default function TradeBranding() {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/trade/white-label").then(({ data }) => {
      setForm({ ...DEFAULT, ...data });
    }).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200_000) {
      toast.error("Logo too large — keep it under 200 KB (optimise or compress first).");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/trade/white-label", form);
      toast.success("Branding saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <PortalShell title="White-label branding" subtitle="Configure your logo, colours and contact details for certificates you generate.">
      <div data-testid="trade-branding-page" className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-3 space-y-5">
          <Card title="Brand" icon={ImageIcon}>
            <div className="space-y-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Brand name</Label>
                <Input data-testid="brand-name" value={form.brand_name} onChange={set("brand_name")} placeholder="e.g. Northshore Plumbing" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Logo</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  {form.logo_data_url ? (
                    <div className="relative">
                      <img src={form.logo_data_url} alt="Logo" className="w-24 h-24 rounded-xl object-contain bg-slate-50 border border-slate-200" />
                      <button onClick={() => setForm({ ...form, logo_data_url: "" })} className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-rose-500 text-white"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <div className="w-24 h-24 rounded-xl bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-slate-400"><ImageIcon className="w-5 h-5" /></div>
                  )}
                  <div>
                    <input data-testid="brand-logo-input" ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={handleLogo} className="hidden" />
                    <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-full text-sm">Upload logo</Button>
                    <p className="text-[10px] text-slate-500 mt-1.5">PNG / JPG / SVG · max 200 KB</p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Colours" icon={Palette}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Primary</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input data-testid="brand-primary" type="color" value={form.primary_color} onChange={set("primary_color")} className="w-12 h-10 rounded-lg border border-slate-200" />
                  <Input value={form.primary_color} onChange={set("primary_color")} className="font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700">Accent</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <input data-testid="brand-accent" type="color" value={form.accent_color} onChange={set("accent_color")} className="w-12 h-10 rounded-lg border border-slate-200" />
                  <Input value={form.accent_color} onChange={set("accent_color")} className="font-mono text-xs" />
                </div>
              </div>
            </div>
          </Card>

          <Card title="Contact" icon={Phone}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />Phone</Label>
                  <Input data-testid="brand-phone" value={form.contact_phone} onChange={set("contact_phone")} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />Email</Label>
                  <Input data-testid="brand-email" type="email" value={form.contact_email} onChange={set("contact_email")} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Address</Label>
                <Input data-testid="brand-address" value={form.contact_address} onChange={set("contact_address")} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" />Website</Label>
                <Input data-testid="brand-website" value={form.website} onChange={set("website")} placeholder="https://..." className="mt-1.5" />
              </div>
            </div>
          </Card>

          <Card title="Disclaimer" icon={FileText}>
            <textarea
              data-testid="brand-disclaimer"
              value={form.disclaimer}
              onChange={set("disclaimer")}
              rows={5}
              placeholder="Optional disclaimer text to appear at the bottom of certificates."
              className="w-full rounded-xl border-slate-200 text-sm p-3"
            />
          </Card>

          <Button data-testid="brand-save" onClick={save} disabled={saving} size="lg" className="btn-aqua rounded-full font-semibold w-full">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving..." : "Save branding"}
          </Button>
        </div>

        {/* Preview */}
        <div className="lg:col-span-2">
          <div className="sticky top-24">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">Live preview</div>
            <div data-testid="brand-preview" className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
              <div className="flex items-start justify-between gap-3 pb-4 border-b-2" style={{ borderColor: form.primary_color }}>
                <div className="min-w-0 flex-1">
                  {form.logo_data_url && (
                    <img src={form.logo_data_url} alt="Logo" className="h-12 mb-3 object-contain" />
                  )}
                  <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: form.primary_color }}>
                    {form.brand_name || "Your Brand Name"}
                  </div>
                  <div className="font-display font-bold text-slate-900 mt-1">Conveyancing Water Certificate™</div>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-[11px] text-slate-500">
                {form.contact_phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" />{form.contact_phone}</div>}
                {form.contact_email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />{form.contact_email}</div>}
                {form.contact_address && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />{form.contact_address}</div>}
                {form.website && <div className="flex items-center gap-1.5"><Globe className="w-3 h-3" />{form.website}</div>}
              </div>
              {form.disclaimer && (
                <p className="mt-5 text-[9px] text-slate-400 leading-relaxed border-t border-slate-100 pt-3">{form.disclaimer}</p>
              )}
            </div>
            <p className="mt-3 text-xs text-slate-500 leading-relaxed">
              This is approximately how your branded certificates will render. The full PDF includes Water Health Score™, historical testing summary, certificate status, verification QR code and more.
            </p>
          </div>
        </div>
      </div>
    </PortalShell>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-cyan-700" />
        <h3 className="font-display font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}
